import React from 'react'

import { useUsdcToCad } from '@/hooks/useCpn'
import { calculateTotalFeeInCad } from '@/utils/feeCalculations'
import { formatCurrency } from '@/utils/payment'

interface TotalFeeInCadProps {
  providerFeeAmount: number
  sendAmount: number
  originalCurrency: string
  cadUsdcRate?: number
  fallbackToHook?: boolean
  platformFeePercentage?: number
  cadAmount?: number // CAD amount from API response
}

export const TotalFeeInCad: React.FC<TotalFeeInCadProps> = ({
  providerFeeAmount,
  sendAmount,
  originalCurrency,
  cadUsdcRate,
  fallbackToHook = false,
  platformFeePercentage = 0.6,
  cadAmount,
}) => {
  // ALWAYS call hooks first before any conditional logic
  const totalFeeUsdc =
    providerFeeAmount + (sendAmount * platformFeePercentage) / 100
  const { cadAmount: hookCadAmount, isLoading } = useUsdcToCad(
    totalFeeUsdc.toString(),
    {
      enabled:
        fallbackToHook &&
        originalCurrency === 'USDC' &&
        !cadUsdcRate &&
        !cadAmount,
    }
  )

  // For non-USDC currencies, calculate in original currency
  if (originalCurrency !== 'USDC') {
    const platformFee = Math.max((sendAmount * platformFeePercentage) / 100, 5)
    const totalFee = providerFeeAmount + platformFee
    return formatCurrency(totalFee, originalCurrency)
  }

  if (fallbackToHook && !cadUsdcRate && !cadAmount && isLoading) {
    return 'Loading...'
  }

  // If cadAmount is provided from API, calculate fee based on that
  if (cadAmount) {
    // Platform fee is 0.6% of the CAD amount (minimum $5 CAD)
    const platformFeeInCad = Math.max(
      (cadAmount * platformFeePercentage) / 100,
      5
    )
    // Convert provider fee from USDC to CAD using the implied exchange rate
    const impliedRate = cadAmount / sendAmount
    const providerFeeInCad = providerFeeAmount * impliedRate
    // Total fee = platform fee + provider fee
    const totalFeeInCad = platformFeeInCad + providerFeeInCad
    return formatCurrency(totalFeeInCad, 'CAD')
  }

  // Use exchange rate if available, otherwise fall back to hook
  const totalFeeInCad = cadUsdcRate
    ? calculateTotalFeeInCad(
        providerFeeAmount,
        sendAmount,
        cadUsdcRate,
        platformFeePercentage
      )
    : hookCadAmount ||
      calculateTotalFeeInCad(
        providerFeeAmount,
        sendAmount,
        undefined,
        platformFeePercentage
      )

  return formatCurrency(totalFeeInCad, 'CAD')
}
